import type { Building } from "building-types/building/buildingTypes";
import { getSourceRoomId } from "building-types/components/componentInstanceUtils";
import type { FloorAssembly, FloorAssemblyOverride } from "building-types/floors/floorAssemblyTypes";
import { FloorAssemblyType } from "building-types/floors/floorAssemblyTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type {
  RoomClassFloorAssemblyGroupTable,
  RWRoomClassFloorAssemblyGroupTable,
} from "building-types/rooms/roomClassFloorAssemblyGroupTable";
import type { RWTable } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { FloorAssemblyHashParamSet } from "doc/elements/floor/floorAssembly";
import { floorAssemblyHash, makeRoomFloorAssembly } from "doc/elements/floor/floorAssembly";
import { evalFormulas, evaluateEntityForComponentInstance, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";
import { roomOpenToBelow } from "../utils/roomOpenToBelow";

export function select({
  optionChoices,
  entityDisabledByGroup,
  rooms,
  doc: {
    floors,
    optionSets,
    instanceOptionSets,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
  },
}: Pick<Building, "optionChoices" | "entityDisabledByGroup" | "rooms" | "doc">) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    rooms,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    floors,
    optionChoices: resolveOptionsInFormula({ floors }, definitions, optionChoices),
    rawOptionChoices: optionChoices,
    definitions,
    entityDisabledByGroup,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  definitions: "shallow",
  floors: "deep",
  optionChoices: "shallow",
});

interface GenerateRoomFloorAssembliesResult {
  roomFloorAssemblies: RWTable<FloorAssembly>;
  roomClassFloorAssemblyGroups: RoomClassFloorAssemblyGroupTable;
}

function generate(options: ReturnType<typeof select>): GenerateRoomFloorAssembliesResult {
  return makeRoomClassFloorAssembliesAndGroups(options);
}

export default memo(select, generate, compare);

/**
 * Evaluate a floor assembly override using component-instance-aware option
 * resolution when the room belongs to a component instance, falling back to
 * the archetype-evaluated entity (which already had group disabling applied)
 * for non-component rooms.
 */
function evaluateFloorForRoom(
  rawFloor: FloorAssemblyOverride,
  archetypeFloor: FloorAssemblyOverride,
  componentInstanceChain: readonly string[] | undefined,
  definitions: OptionDefinitions,
  rawOptionChoices: OptionChoices,
): FloorAssemblyOverride {
  if (componentInstanceChain && componentInstanceChain.length > 0) {
    return evaluateEntityForComponentInstance(rawFloor, componentInstanceChain, definitions, rawOptionChoices);
  }
  return archetypeFloor;
}

export function makeRoomClassFloorAssembliesAndGroups(
  {
    rooms,
    frameFloorAssemblies,
    slabFloorAssemblies,
    framingComponents,
    slabComponents,
    sheathingComponents,
    floors: rawFloors,
    optionChoices,
    rawOptionChoices,
    definitions,
    entityDisabledByGroup,
  }: ReturnType<typeof select>,
  hashParamsToInclude?: FloorAssemblyHashParamSet,
): GenerateRoomFloorAssembliesResult {
  const roomFloorAssemblies: RWTable<FloorAssembly> = {};
  const roomClassFloorAssemblyGroups: RWRoomClassFloorAssemblyGroupTable = {};

  if (!rooms) {
    return { roomFloorAssemblies, roomClassFloorAssemblyGroups };
  }

  const archetypeFloors = evalFormulas(rawFloors, (v) => optionChoices[v], undefined, entityDisabledByGroup);

  for (const roomId in rooms) {
    const room = rooms[roomId];

    const resolvedRoomAttributes = { ...room.attributes, openToBelow: roomOpenToBelow(room) };

    const baseRoomId = getSourceRoomId(room);
    const componentInstanceChain = room.componentTransform?.componentInstanceChain;
    const roomFloors = Object.keys(rawFloors)
      .filter((floorId) => rawFloors[floorId].parentId === baseRoomId)
      .map((floorId) =>
        evaluateFloorForRoom(
          rawFloors[floorId],
          archetypeFloors[floorId],
          componentInstanceChain,
          definitions,
          rawOptionChoices,
        ),
      )
      .filter((floor) => !floor.disabled)
      .sort((a, b) => a.sortIndex - b.sortIndex);

    const floorAssemblyOverride = roomFloors[0];

    const roomFloorAssembly = makeRoomFloorAssembly({
      level: room.level,
      roomAttributes: resolvedRoomAttributes,
      frameFloorAssemblies,
      slabFloorAssemblies,
      framingComponents,
      slabComponents,
      sheathingComponents,
      floorAssemblyOverride,
    });

    assertIsNotUndefined(roomFloorAssembly);
    roomFloorAssemblies[room.id] = roomFloorAssembly;

    if (roomFloorAssembly.assemblyType !== FloorAssemblyType.VOID) {
      //note this hash must be expanded anytime we add a condition which requires a new floor assembly
      //currently we have roomClass, topDatumOffset, spanDirection, and assemblyId as our conditions
      const roomClass = resolvedRoomAttributes.roomClass;
      const { hash, hashParams } = floorAssemblyHash(resolvedRoomAttributes, roomFloorAssembly, hashParamsToInclude);

      if (!roomClassFloorAssemblyGroups[roomClass]) {
        roomClassFloorAssemblyGroups[roomClass] = {};
      }

      const classGroup = roomClassFloorAssemblyGroups[roomClass];

      if (!classGroup[hash]) {
        classGroup[hash] = { hashParams, roomIds: [] };
      }

      classGroup[hash].roomIds.push(room.id);
    }
  }

  return { roomFloorAssemblies, roomClassFloorAssemblyGroups };
}
